import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import {
  lookupContract,
  updateSelection,
  confirmDepositTransfer,
  confirmBalanceTransfer,
  type PublicContract,
  type PlanType,
} from '../contract/contractApi';
import { requestBillingAuthCheckout } from './paymentApi';
import { getTossPayments } from './tossClient';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import {
  OPTIONAL_APPS,
  PLAN_TIERS,
  computeMonthlyFee,
  findPlanTier,
  formatManwon,
} from '../../price/pricingData';
import './Payment.scss';

// 토스페이먼츠 홈페이지 심사용 "API 개별 연동 키"(자체창) 테스트 클라이언트 키.
// 계약 조회 없이 바로 결제창을 띄워보기 위한 용도이며, 실제 결제 승인(서버 confirm)은 호출하지 않는다.
// 심사 통과 후 계약 기반 결제 플로우로 되돌릴 때 이 키와 REVIEW_* 관련 코드는 제거할 것.
const TOSS_REVIEW_CLIENT_KEY = 'test_ck_AQ92ymxN34N9PooLqlGOrajRKXvd';

interface LookupFormState {
  customerName: string;
  phone: string;
}

type LookupErrors = Partial<Record<keyof LookupFormState, string>>;

const PHONE_PATTERN = /^[0-9+\-()\s]{8,20}$/;

const INITIAL_LOOKUP: LookupFormState = {
  customerName: '',
  phone: '',
};

// 결제 단계에서 선택 항목을 바꿀 때 보여주는 예상 금액은 pricingData.ts로 계산하고,
// 실제 저장/청구 금액은 서버(server/routers/common/pricing.js)가 재계산한다.
const APP_KEYS: string[] = OPTIONAL_APPS.map((app) => app.key);

// 신청서에서 고른 추가 앱 목록. 옛 신청은 같은 칸에 관리자용 탭(교적관리 등)이 들어 있어 앱 이름만 남기고,
// 주일학교앱은 예전 방식(sundayschool_addon)으로만 표시된 경우도 포함한다.
function selectedAppsOf(c: PublicContract): string[] {
  const apps = (c.selected_admin_tabs || []).filter((item) => APP_KEYS.includes(item));
  if (c.sundayschool_addon && !apps.includes('주일학교앱')) apps.push('주일학교앱');
  return apps;
}

interface SelectionFormState {
  memberSize: string;
  apps: string[];
}

const Payment: React.FC = () => {
  const [lookup, setLookup] = useState<LookupFormState>(INITIAL_LOOKUP);
  const [lookupErrors, setLookupErrors] = useState<LookupErrors>({});
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [selectionForm, setSelectionForm] = useState<SelectionFormState | null>(null);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [depositPeriodAgreed, setDepositPeriodAgreed] = useState(false);
  const [finalPeriodAgreed, setFinalPeriodAgreed] = useState(false);

  // 토스페이먼츠 심사용 상태 - 계약 조회 없이 /price 요금표 그대로 옵션을 골라 결제창을 띄운다.
  const [searchParams] = useSearchParams();
  const reviewResult = searchParams.get('review');
  const reviewResultMessage = searchParams.get('message');
  const [reviewPlanType, setReviewPlanType] = useState<PlanType>('subscription');
  const [reviewSelectedTabs, setReviewSelectedTabs] = useState<string[]>([]);
  const [reviewPaying, setReviewPaying] = useState(false);
  const [reviewError, setReviewError] = useState('');

  // 심사용 금액은 가장 작은 규모 요금 기준
  const REVIEW_TIER = PLAN_TIERS[0];
  const REVIEW_OPTIONAL_TABS = APP_KEYS;
  const reviewAmount = computeMonthlyFee(REVIEW_TIER.size, reviewSelectedTabs) ?? REVIEW_TIER.base;

  const toggleReviewTab = (tab: string) => {
    setReviewSelectedTabs((prev) => (prev.includes(tab) ? prev.filter((t) => t !== tab) : [...prev, tab]));
  };

  useEffect(() => {
    const nodes = document.querySelectorAll('.payment-page .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const updateLookup = <K extends keyof LookupFormState>(key: K, value: LookupFormState[K]) => {
    setLookup((prev) => ({ ...prev, [key]: value }));
    setLookupErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateLookup = (): LookupErrors => {
    const nextErrors: LookupErrors = {};
    if (!lookup.customerName.trim()) nextErrors.customerName = '고객명을 입력해 주세요.';
    if (!lookup.phone.trim()) {
      nextErrors.phone = '전화번호를 입력해 주세요.';
    } else if (!PHONE_PATTERN.test(lookup.phone.trim())) {
      nextErrors.phone = '올바른 연락처 형식을 입력해 주세요.';
    }
    return nextErrors;
  };

  const performLookup = async () => {
    const validationErrors = validateLookup();
    if (Object.keys(validationErrors).length > 0) {
      setLookupErrors(validationErrors);
      return;
    }

    setIsLookingUp(true);
    setLookupErrors({});
    setPayError('');
    try {
      const found = await lookupContract(lookup.customerName.trim(), lookup.phone.trim());
      setContract(found);
      setSelectionForm(buildSelectionForm(found));
    } catch (error) {
      setContract(null);
      setLookupErrors({
        customerName: error instanceof Error ? error.message : '계약 조회에 실패했습니다.',
      });
    } finally {
      setIsLookingUp(false);
      setHasSearched(true);
    }
  };

  const handleLookup = async (event: FormEvent) => {
    event.preventDefault();
    void performLookup();
  };

  const renderSelectionSummary = (c: PublicContract) => (
    <>
      <div className="payment-summary-card__row">
        <span className="payment-summary-card__label">교회명</span>
        <span className="payment-summary-card__value">
          {c.company_name || c.contract_name || '-'}
        </span>
      </div>
      <div className="payment-summary-card__row">
        <span className="payment-summary-card__label">성도 규모</span>
        <span className="payment-summary-card__value">{c.member_size || '-'}</span>
      </div>
      <div className="payment-summary-card__row">
        <span className="payment-summary-card__label">요금제</span>
        <span className="payment-summary-card__value">
          {c.plan_type === 'subscription' ? '월 요금(구독)' : c.plan_type === 'purchase' ? '구축제' : '-'}
        </span>
      </div>
      <div className="payment-summary-card__row">
        <span className="payment-summary-card__label">사용 앱</span>
        <span className="payment-summary-card__value">
          {['성도용 앱 + PC 관리자', ...selectedAppsOf(c)].join(', ')}
        </span>
      </div>
      {c.monthly_amount != null && (
        <div className="payment-summary-card__row">
          <span className="payment-summary-card__label">월 요금</span>
          <span className="payment-summary-card__value">
            {c.monthly_amount.toLocaleString()}원 / 월
          </span>
        </div>
      )}
    </>
  );

  const renderServicePeriodNotice = (
    planType: PlanType | undefined,
    agreed: boolean,
    onAgreedChange: (checked: boolean) => void
  ) => (
    <div className="payment-period-notice">
      <p className="payment-period-notice__title">서비스 제공 기간 안내 및 개인정보처리 방침</p>
      <p className="payment-period-notice__desc">
        {planType === 'purchase'
          ? '구축제는 계약서에 명시된 서비스 제공 기간 동안 앱이 제공되며, 기간 만료 시 별도 안내에 따라 갱신 여부를 결정합니다.'
          : '구독제는 결제일을 기준으로 매월 자동 갱신되며, 별도로 해지를 신청하기 전까지 서비스가 계속 제공됩니다.'}
      </p>
      <p className="payment-period-notice__desc">
        또한 등록된 개인정보는 해지 후 3개월까지만 보관되며, 그 이후 삭제 처리됩니다.
      </p>
      <label className="payment-checkbox">
        <input type="checkbox" checked={agreed} onChange={(e) => onAgreedChange(e.target.checked)} />
        확인했습니다
      </label>
    </div>
  );

  const buildSelectionForm = (c: PublicContract): SelectionFormState => ({
    // 옛 신청의 예전 규모 문자열은 선택지에 없으므로 비워서 다시 고르게 한다.
    memberSize: findPlanTier(c.member_size || '') ? c.member_size || '' : '',
    apps: selectedAppsOf(c),
  });

  const resetSelection = () => {
    if (!contract) return;
    setSelectionForm(buildSelectionForm(contract));
    setSelectionError('');
  };

  const toggleSelectionApp = (app: string) => {
    setSelectionForm((prev) =>
      prev
        ? {
            ...prev,
            apps: prev.apps.includes(app) ? prev.apps.filter((item) => item !== app) : [...prev.apps, app],
          }
        : prev
    );
  };

  const saveSelection = async (formOverride?: SelectionFormState) => {
    const form = formOverride || selectionForm;
    if (!contract || !form) return;
    setSelectionError('');
    setIsSavingSelection(true);
    try {
      const updated = await updateSelection(contract.id, contract.source, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
        memberSize: form.memberSize,
        apps: form.apps,
      });
      setContract(updated);
      setSelectionForm(buildSelectionForm(updated));
    } catch (error) {
      setSelectionError(error instanceof Error ? error.message : '선택 항목 변경에 실패했습니다.');
    } finally {
      setIsSavingSelection(false);
    }
  };

  const renderSelectionEditor = () => {
    if (!selectionForm) return null;
    const tier = findPlanTier(selectionForm.memberSize);
    const monthly = computeMonthlyFee(selectionForm.memberSize, selectionForm.apps);

    return (
      <div className="payment-edit">
        <div className="payment-edit__field">
          <label className="payment-edit__label">성도 규모</label>
          <select
            className="payment-edit__select"
            value={selectionForm.memberSize}
            onChange={(e) =>
              setSelectionForm((prev) => (prev ? { ...prev, memberSize: e.target.value } : prev))
            }
          >
            <option value="">선택해주세요</option>
            {PLAN_TIERS.map((item) => (
              <option key={item.size} value={item.size}>
                {item.size}
              </option>
            ))}
          </select>
        </div>

        <div className="payment-edit__field">
          <label className="payment-edit__label">사용할 앱</label>
          <p className="payment-edit__hint">
            성도용 앱 + PC 관리자 (기본{tier ? ` · 월 ${formatManwon(tier.base)}` : ''})
          </p>
          <div className="payment-checkbox-grid">
            {OPTIONAL_APPS.map((app) => (
              <label key={app.key} className="payment-checkbox">
                <input
                  type="checkbox"
                  checked={selectionForm.apps.includes(app.key)}
                  onChange={() => toggleSelectionApp(app.key)}
                />
                {app.label}
                {tier ? ` (+${formatManwon(tier.apps[app.key])}/월)` : ''}
              </label>
            ))}
          </div>
        </div>

        <div className="payment-edit__estimate">
          <span>예상 월 정기결제 금액</span>
          <span>{monthly != null ? `${monthly.toLocaleString()}원 / 월` : '성도 규모를 선택해 주세요'}</span>
        </div>

        {selectionError && <p className="payment-edit__error">{selectionError}</p>}

        <div className="payment-edit__actions">
          <button
            type="button"
            className="payment-btn payment-btn--primary payment-btn--sm"
            onClick={() => saveSelection()}
            disabled={isSavingSelection || !selectionForm.memberSize}
          >
            {isSavingSelection ? '저장 중...' : '변경사항 저장'}
          </button>
          <button
            type="button"
            className="payment-btn payment-btn--secondary payment-btn--sm"
            onClick={resetSelection}
            disabled={isSavingSelection}
          >
            초기화
          </button>
        </div>
      </div>
    );
  };

  const handleConfirmDepositTransfer = async () => {
    if (!contract) return;
    if (!window.confirm('계좌입금을 완료하셨나요? 확인을 누르면 입금완료로 처리됩니다.')) return;
    setPayError('');
    setIsPaying(true);
    try {
      const updated = await confirmDepositTransfer(contract.id, contract.source, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
      });
      setContract(updated);
      setSelectionForm(buildSelectionForm(updated));
    } catch (error) {
      setPayError(error instanceof Error ? error.message : '입금 확인 처리에 실패했습니다.');
    } finally {
      setIsPaying(false);
    }
  };

  const handleConfirmBalanceTransfer = async () => {
    if (!contract) return;
    if (!window.confirm('잔금 계좌입금을 완료하셨나요? 확인을 누르면 최종결제 완료로 처리됩니다.')) return;
    setPayError('');
    setIsPaying(true);
    try {
      const updated = await confirmBalanceTransfer(contract.id, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
      });
      setContract(updated);
      setSelectionForm(buildSelectionForm(updated));
    } catch (error) {
      setPayError(error instanceof Error ? error.message : '잔금 입금 확인 처리에 실패했습니다.');
    } finally {
      setIsPaying(false);
    }
  };

  const handleBillingAuth = async () => {
    if (!contract) return;
    setPayError('');
    setIsPaying(true);
    try {
      const checkout = await requestBillingAuthCheckout(contract.id, contract.source);
      const tossPayments = await getTossPayments(checkout.clientKey);
      await tossPayments.requestBillingAuth('카드', {
        customerKey: checkout.customerKey,
        // Toss의 requestBillingAuth successUrl 리다이렉트에는 customerKey/authKey만 붙고
        // orderId는 절대 붙지 않는다(카드 등록은 특정 주문과 무관한 절차라서) - billing-auth/confirm이
        // payment_transactions를 order_id로 찾아야 하므로 orderId를 직접 successUrl 쿼리에 실어 보낸다.
        successUrl: `${window.location.origin}/payment/success?orderId=${encodeURIComponent(checkout.orderId)}`,
        failUrl: `${window.location.origin}/payment/fail`,
      });
    } catch (error) {
      setPayError(error instanceof Error ? error.message : '정기결제 등록을 시작하지 못했습니다.');
      setIsPaying(false);
    }
  };

  const handleReviewPay = async () => {
    setReviewError('');
    setReviewPaying(true);
    try {
      const tossPayments = await getTossPayments(TOSS_REVIEW_CLIENT_KEY);
      const orderName =
        reviewSelectedTabs.length > 0
          ? `교회 앱 제작 서비스 기본 패키지 외 ${reviewSelectedTabs.length}건`
          : '교회 앱 제작 서비스 기본 패키지';

      if (reviewPlanType === 'subscription') {
        // 정기결제: 카드 등록(빌링키 발급)만 진행 - 실제 월 자동청구는 계약 연동 후 서버 cron이 처리.
        await tossPayments.requestBillingAuth('카드', {
          customerKey: `review-${Date.now()}`,
          successUrl: `${window.location.origin}/payment?review=success&mode=subscription`,
          failUrl: `${window.location.origin}/payment?review=fail`,
        });
      } else {
        // 단건결제: 선택한 옵션을 합산한 금액을 즉시 결제.
        await tossPayments.requestPayment('카드', {
          amount: reviewAmount,
          orderId: `review-${Date.now()}`,
          orderName,
          customerName: '심사용 결제',
          successUrl: `${window.location.origin}/payment?review=success&mode=purchase`,
          failUrl: `${window.location.origin}/payment?review=fail`,
        });
      }
    } catch (error) {
      setReviewError(error instanceof Error ? error.message : '결제창을 여는 데 실패했습니다.');
      setReviewPaying(false);
    }
  };

  /* 결제 유형 선택 화면(2버튼) 임시 비활성화 — 위 paymentCategory 기본값으로 대체
  if (!paymentCategory) {
    return (
      <div className="payment-page">
        <Header />
        <section className="page-header">
          <div className="container reveal in">
            <div className="breadcrumb">
              <Link to="/">Home</Link> <span>· 서비스신청 · 결제</span>
            </div>
            <div className="page-header-grid">
              <div>
                <h1 className="page-title">
                  어떤 <span className="em">결제</span>를
                  <br />
                  진행하시나요?
                </h1>
                <p className="page-sub">
                  진행 단계에 따라 결제 유형을 선택해 주세요.
                </p>
              </div>
              <div className="page-meta">Secure Payment</div>
            </div>
          </div>
        </section>

        <section className="payment-section">
          <div className="container">
            <div className="payment-category-grid">
              {PAYMENT_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className="payment-category-card"
                  onClick={() => setPaymentCategory(cat.id)}
                >
                  <span className="payment-category-card__tag">{cat.tag}</span>
                  <h2 className="payment-category-card__title">{cat.title}</h2>
                  <p className="payment-category-card__desc">{cat.desc}</p>
                  <span className="payment-category-card__arrow">→</span>
                </button>
              ))}
            </div>
          </div>
        </section>
        <Footer />
        <ScrollToTopButton />
      </div>
    );
  }
  */

  return (
    <div className="payment-page">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· 서비스신청 · 결제</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                계약 확인 후,
                <br />
                <span className="em">결제</span>를 진행하세요.
              </h1>
              <p className="page-sub">
                서명이 완료된 계약의 고객명과 전화번호를 입력하면 결제 금액을 확인할 수
                있습니다.
              </p>
            </div>
            <div className="page-meta">Secure Payment</div>
          </div>
        </div>
      </section>

      <section className="payment-section">
        <div className="container">
          {!contract && (
            <div className="payment-panel">
              <h2 className="payment-panel__title">계약 조회</h2>
              <p className="payment-panel__desc">
                계약 시 등록한 고객명과 전화번호를 입력해 결제할 계약을 불러와 주세요.
              </p>

              <form className="payment-form" onSubmit={handleLookup} noValidate>
                <div className="payment-form__grid">
                  <div className="payment-field payment-field--inline">
                    <label className="payment-field__label" htmlFor="payment-customer">
                      고객명 <span className="payment-field__required">*</span>
                    </label>
                    <input
                      id="payment-customer"
                      className={`payment-field__input ${
                        lookupErrors.customerName ? 'payment-field__input--error' : ''
                      }`}
                      type="text"
                      value={lookup.customerName}
                      onChange={(e) => updateLookup('customerName', e.target.value)}
                      placeholder="홍길동"
                      autoComplete="name"
                    />
                    {lookupErrors.customerName && (
                      <p className="payment-field__error">{lookupErrors.customerName}</p>
                    )}
                  </div>

                  <div className="payment-field payment-field--inline">
                    <label className="payment-field__label" htmlFor="payment-phone">
                      전화번호 <span className="payment-field__required">*</span>
                    </label>
                    <PhoneNumberInput
                      id="payment-phone"
                      className={`payment-field__input ${
                        lookupErrors.phone ? 'payment-field__input--error' : ''
                      }`}
                      value={lookup.phone}
                      onChange={(phone) => updateLookup('phone', phone)}
                      autoComplete="tel"
                    />
                    {lookupErrors.phone && (
                      <p className="payment-field__error">{lookupErrors.phone}</p>
                    )}
                  </div>
                </div>

                <button type="submit" className="payment-btn payment-btn--primary" disabled={isLookingUp}>
                  {isLookingUp ? '조회 중...' : '계약 조회'}
                </button>
              </form>
            </div>
          )}

          {hasSearched && contract && contract.status === 'applied' && (() => {
            const effectivePlanType = contract.plan_type;
            return (
            <div className="payment-panel">
              <h2 className="payment-panel__title">기본요금 선결제</h2>
              <p className="payment-panel__desc">
                {effectivePlanType === 'subscription'
                  ? '신청 확정 및 MVP 제작 착수를 위한 기본요금은 토스페이먼츠 정기결제로 진행됩니다.'
                  : '신청 확정 및 MVP 제작 착수를 위한 기본요금은 계좌입금으로 진행됩니다.'}
              </p>

              <div className="payment-field payment-field--full">
                <span className="payment-field__label">주문자 정보</span>
                <div className="payment-summary-card">
                  <div className="payment-summary-card__row">
                    <span className="payment-summary-card__label">고객명</span>
                    <span className="payment-summary-card__value">{contract.customer_name || '-'}</span>
                  </div>
                  {renderSelectionSummary(contract)}
                  <div className="payment-summary-card__row">
                    <span className="payment-summary-card__label">기본요금</span>
                    <span className="payment-summary-card__value">
                      {contract.deposit_amount?.toLocaleString() ?? '-'}원
                    </span>
                  </div>
                </div>
              </div>

              {renderSelectionEditor()}

              {renderServicePeriodNotice(effectivePlanType ?? undefined, depositPeriodAgreed, setDepositPeriodAgreed)}

              {effectivePlanType === 'subscription' ? (
                <div className="payment-actions">
                  <button
                    type="button"
                    className="payment-btn payment-btn--primary"
                    onClick={handleBillingAuth}
                    disabled={isPaying || !depositPeriodAgreed}
                  >
                    {isPaying ? '결제창 여는 중...' : '결제하기'}
                  </button>
                  {payError && (
                    <p className="payment-preparing-notice" role="status">
                      {payError}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="payment-bank-transfer-notice">
                    <p className="payment-bank-transfer-notice__title">기본요금 계좌입금 안내</p>
                    <p className="payment-bank-transfer-notice__desc">
                      아래 계좌로 기본요금 {contract.deposit_amount?.toLocaleString() ?? '-'}원을 입금해 주세요. 입금
                      후 아래 "입금완료" 버튼을 눌러 주시면 신청이 다음 단계로 진행됩니다.
                    </p>
                    <p className="payment-bank-transfer-notice__account">
                      {contract.provider_account_number || '-'}
                    </p>
                  </div>

                  <div className="payment-actions">
                    <button
                      type="button"
                      className="payment-btn payment-btn--primary"
                      onClick={handleConfirmDepositTransfer}
                      disabled={isPaying || !depositPeriodAgreed}
                    >
                      {isPaying ? '처리 중...' : '입금완료'}
                    </button>
                    {payError && (
                      <p className="payment-preparing-notice" role="status">
                        {payError}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            );
          })()}

          {hasSearched &&
            contract &&
            ['deposit_paid', 'mvp_ready', 'mvp_confirmed'].includes(contract.status) && (
              <div className="payment-panel payment-notice" role="status">
                <h2 className="payment-panel__title">아직 결제 단계가 아닙니다</h2>
                <p className="payment-panel__desc">
                  MVP 제작 및 계약서 서명이 먼저 완료되어야 최종결제를 진행할 수 있습니다.
                </p>
                <Link to="/mvp-confirm" className="payment-btn payment-btn--secondary">
                  MVP 확인하러 가기
                </Link>
              </div>
            )}

          {hasSearched && contract && contract.status === 'signed' && (() => {
            const effectivePlanType = contract.plan_type;
            return (
            <div className="payment-panel">
              <h2 className="payment-panel__title">최종결제</h2>
              <p className="payment-panel__desc">
                {effectivePlanType === 'subscription'
                  ? '선택하신 구독형 요금제의 정기결제를 등록해 주세요.'
                  : '선택하신 구매형 요금제의 잔금은 계좌입금으로 진행됩니다.'}
              </p>

              <div className="payment-field payment-field--full">
                <span className="payment-field__label">주문자 정보</span>
                <div className="payment-summary-card">
                  <div className="payment-summary-card__row">
                    <span className="payment-summary-card__label">고객명</span>
                    <span className="payment-summary-card__value">{contract.customer_name || '-'}</span>
                  </div>
                  {renderSelectionSummary(contract)}
                  {effectivePlanType === 'subscription' ? (
                    <div className="payment-summary-card__row">
                      <span className="payment-summary-card__label">월 정기결제 금액</span>
                      <span className="payment-summary-card__value">
                        {contract.monthly_amount?.toLocaleString() ?? '-'}원 / 월
                      </span>
                    </div>
                  ) : (
                    <div className="payment-summary-card__row">
                      <span className="payment-summary-card__label">잔금</span>
                      <span className="payment-summary-card__value">
                        {contract.balance_amount?.toLocaleString() ?? '-'}원
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {renderSelectionEditor()}

              {renderServicePeriodNotice(
                effectivePlanType ?? undefined,
                finalPeriodAgreed,
                setFinalPeriodAgreed
              )}

              {effectivePlanType === 'subscription' ? (
                <div className="payment-actions">
                  <button
                    type="button"
                    className="payment-btn payment-btn--primary"
                    onClick={handleBillingAuth}
                    disabled={isPaying || !finalPeriodAgreed}
                  >
                    {isPaying ? '결제창 여는 중...' : '결제하기'}
                  </button>
                  {payError && (
                    <p className="payment-preparing-notice" role="status">
                      {payError}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="payment-bank-transfer-notice">
                    <p className="payment-bank-transfer-notice__title">잔금 계좌입금 안내</p>
                    <p className="payment-bank-transfer-notice__desc">
                      아래 계좌로 잔금 {contract.balance_amount?.toLocaleString() ?? '-'}원을 입금해 주세요. 입금
                      후 아래 "입금완료" 버튼을 눌러 주시면 최종결제 완료로 처리됩니다.
                    </p>
                    <p className="payment-bank-transfer-notice__account">
                      {contract.provider_account_number || '-'}
                    </p>
                  </div>

                  <div className="payment-actions">
                    <button
                      type="button"
                      className="payment-btn payment-btn--primary"
                      onClick={handleConfirmBalanceTransfer}
                      disabled={isPaying || !finalPeriodAgreed}
                    >
                      {isPaying ? '처리 중...' : '입금완료'}
                    </button>
                    {payError && (
                      <p className="payment-preparing-notice" role="status">
                        {payError}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            );
          })()}

          {hasSearched && contract && contract.status === 'completed' && (
            <div className="payment-panel payment-notice" role="status">
              <h2 className="payment-panel__title">결제가 완료되었습니다</h2>
              <p className="payment-panel__desc">이미 최종결제까지 완료된 신청입니다.</p>
            </div>
          )}

          {/* 토스페이먼츠 홈페이지 심사용 결제 페이지 - 심사 완료 후 계약 조회 플로우로 되돌리며 잠시 주석 처리합니다.
          <div className="payment-panel">
            <h2 className="payment-panel__title">교회 앱 제작 서비스</h2>

            <div className="payment-edit__field">
              <label className="payment-edit__label">결제 방식</label>
              <div className="payment-plan-options">
                <label
                  className={`payment-plan-option${
                    reviewPlanType === 'subscription' ? ' payment-plan-option--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="review-plan-type"
                    value="subscription"
                    checked={reviewPlanType === 'subscription'}
                    onChange={() => setReviewPlanType('subscription')}
                  />
                  정기결제 (구독형 · 매월 자동결제)
                </label>
                <label
                  className={`payment-plan-option${
                    reviewPlanType === 'purchase' ? ' payment-plan-option--active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="review-plan-type"
                    value="purchase"
                    checked={reviewPlanType === 'purchase'}
                    onChange={() => setReviewPlanType('purchase')}
                  />
                  단건결제 (구매형 · 1회 결제)
                </label>
              </div>
            </div>

            <div className="payment-edit__field">
              <label className="payment-edit__label">추가 옵션</label>
              <div className="payment-checkbox-grid">
                {REVIEW_OPTIONAL_TABS.map((tab) => (
                  <label key={tab} className="payment-checkbox">
                    <input
                      type="checkbox"
                      checked={reviewSelectedTabs.includes(tab)}
                      onChange={() => toggleReviewTab(tab)}
                    />
                    {tab} (
                    {`${REVIEW_TIER.apps[tab as keyof typeof REVIEW_TIER.apps].toLocaleString()}원/월`}
                    )
                  </label>
                ))}
              </div>
            </div>

            <div className="payment-edit__estimate">
              <span>{reviewPlanType === 'subscription' ? '월 정기결제 금액' : '결제 금액'}</span>
              <span>
                {reviewAmount.toLocaleString()}원{reviewPlanType === 'subscription' ? ' / 월' : ''}
              </span>
            </div>

            {reviewResult === 'success' && (
              <p className="payment-preparing-notice" role="status">
                결제창 테스트가 정상적으로 완료되었습니다.
              </p>
            )}
            {reviewResult === 'fail' && (
              <p className="payment-preparing-notice" role="status">
                {reviewResultMessage || '결제가 취소되었거나 실패했습니다.'}
              </p>
            )}

            <div className="payment-actions">
              <button
                type="button"
                className="payment-btn payment-btn--primary"
                onClick={handleReviewPay}
                disabled={reviewPaying}
              >
                {reviewPaying
                  ? '결제창 여는 중...'
                  : reviewPlanType === 'subscription'
                    ? '정기결제 등록하기'
                    : '결제하기'}
              </button>
              {reviewError && (
                <p className="payment-preparing-notice" role="status">
                  {reviewError}
                </p>
              )}
            </div>
          </div>
          */}
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
};

export default Payment;
